import { SFX_RECIPES, scheduleBlip } from './sfxEngine'

/** Overall SFX mix, applied once alongside the saved slider level. */
export const SFX_OUTPUT_GAIN = 0.64

/** Immutable filenames: reviewed WAV edits and the original replacement MP3s. */
export const SFX_FILES = {
  tap: '/audio/sfx/selected-2026-10-06/tap.wav',
  select: '/audio/sfx/selected-2026-10-06/select.wav',
  deselect: '/audio/sfx/selected-2026-10-06/deselect.wav',
  confirm: '/audio/sfx/selected-2026-10-06/confirm.wav',
  coin: '/audio/sfx/selected-2026-10-06/coin.wav',
  victory: '/audio/sfx/selected-2026-10-06/victory.wav',
  goldAppear: '/audio/sfx/selected-2026-10-06/goldAppear.wav',
  trainingAppear: '/audio/sfx/selected-2026-10-06/trainingAppear.wav',
  hit: '/audio/sfx/selected-2026-10-06/hit.wav',
  epic: '/audio/sfx/selected-2026-10-11/epic.mp3',
  legendary: '/audio/sfx/selected-2026-10-11/legendary.mp3',
} as const

export type SfxName = keyof typeof SFX_FILES

/** A cold/missing file uses the old cue immediately while the cache warms.
 * Downloads never replay an interaction after it has passed. */
export function createSfxPlayer(
  ctx: AudioContext, master: AudioNode,
  options: { fetcher?: typeof fetch; now?: () => number; initialVolume?: number } = {},
) {
  const fetcher = options.fetcher ?? fetch
  const now = options.now ?? (() => performance.now())
  const buffers = new Map<SfxName, AudioBuffer>()
  const loading = new Map<SfxName, Promise<void>>()
  const active = new Set<() => void>()
  const channel = ctx.createGain()
  channel.gain.value = Number.isFinite(options.initialVolume) ? Math.max(0, Math.min(1, options.initialVolume!)) : 1
  channel.connect(master)
  let generation = 0

  function load(name: SfxName): Promise<void> {
    if (buffers.has(name)) return Promise.resolve()
    const pending = loading.get(name)
    if (pending) return pending
    const request = (async () => {
      const response = await fetcher(SFX_FILES[name])
      if (!response.ok) throw new Error(`SFX ${name}: ${response.status}`)
      buffers.set(name, await ctx.decodeAudioData(await response.arrayBuffer()))
    })().finally(() => loading.delete(name))
    loading.set(name, request)
    return request
  }

  function track(stop: () => void) {
    if (active.size >= 24) {
      const oldest = active.values().next().value!
      active.delete(oldest)
      oldest()
    }
    active.add(stop)
  }

  function playReady(name: SfxName) {
    try {
      const t0 = ctx.currentTime + 0.005
      const buffer = buffers.get(name)
      if (!buffer) {
        void load(name).catch(() => {})
        for (const spec of SFX_RECIPES[name]!) {
          const stop = scheduleBlip(ctx, channel, spec, t0 + spec.delayMs / 1000, () => active.delete(stop))
          track(stop)
        }
        return
      }
      const source = ctx.createBufferSource()
      const gain = ctx.createGain()
      source.buffer = buffer
      const end = t0 + buffer.duration
      // Keep the reviewed level; only soften cut edges against phone pops.
      const edge = Math.min(0.012, buffer.duration / 2)
      gain.gain.setValueAtTime(0, t0)
      gain.gain.linearRampToValueAtTime(1, t0 + edge)
      gain.gain.setValueAtTime(1, end - edge)
      gain.gain.linearRampToValueAtTime(0, end)
      source.connect(gain)
      gain.connect(channel)
      const stop = () => {
        const t = ctx.currentTime
        const level = gain.gain.value
        gain.gain.cancelScheduledValues(t)
        gain.gain.setValueAtTime(level, t)
        gain.gain.linearRampToValueAtTime(0, t + 0.012)
        try { source.stop(t + 0.012) } catch { /* already ended */ }
      }
      source.onended = () => {
        active.delete(stop)
        source.disconnect()
        gain.disconnect()
      }
      source.start(t0)
      track(stop)
    } catch { /* audio is never worth crashing over */ }
  }

  return {
    preload: () => Promise.all((Object.keys(SFX_FILES) as SfxName[]).map(load)).then(() => {}),
    setVolume(value: number) {
      if (!Number.isFinite(value)) return
      const t = ctx.currentTime
      const level = channel.gain.value
      channel.gain.cancelScheduledValues(t)
      channel.gain.setValueAtTime(level, t)
      channel.gain.linearRampToValueAtTime(Math.max(0, Math.min(1, value)), t + 0.04)
    },
    play(name: SfxName) {
      if (ctx.state === 'running') {
        playReady(name)
        return
      }
      const token = generation
      const requested = now()
      void ctx.resume().then(() => {
        if (token === generation && ctx.state === 'running' && now() - requested < 250) playReady(name)
      }).catch(() => {})
    },
    stopAll() {
      generation++
      for (const stop of active) stop()
      active.clear()
    },
  }
}
